/** @jsxImportSource react */
'use client';

import Image from 'next/image';

import { motion } from 'motion/react';

// Real quotes pulled verbatim from the OnArk customer survey (March 2026).
// Three churches across three different size brackets so a visitor sees
// someone "like them" — small (Harmony Hill), mid (Northwood), mega (Harvest).
// Logos live in /public/logos/. All three respondents are NPS 7+ and answered
// "Yes" to the testimonial-permission question.
interface Testimonial {
  quote: string;
  name: string;
  role: string;
  church: string;
  size: string;
  logo: string;
  logoAlt: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    church: 'Northwood Church',
    logo: '/logos/northwood.webp',
    logoAlt: 'Northwood Church',
    name: 'Bradford',
    quote:
      'Our connections team gets a notification the moment someone finishes their DISC and Gifts test. They follow up and help that person find the next step that fits.',
    role: 'Executive / Operations',
    size: '1,000–2,500 weekly',
  },
  {
    church: 'Harvest',
    logo: '/logos/harvest.png',
    logoAlt: 'Harvest',
    name: 'KC',
    quote:
      'We have a great process for online small group leadership training. In our monthly newsletters, I take responses from completed trainings and share them as highlights with our leaders.',
    role: 'Discipleship / Next Steps',
    size: '5,000+ weekly',
  },
  {
    church: 'Harmony Hill',
    logo: '/logos/harmony-hill.svg',
    logoAlt: 'Harmony Hill Church',
    name: 'Chuck',
    quote:
      'Before OnArk our volunteer onboarding process was a hassle for both our church volunteers and our staff. After finding OnArk the process was streamlined for our staff and made more sense to our new volunteers. The best part is the integration with our church database.',
    role: 'Communications / Tech',
    size: '1,000–2,500 weekly',
  },
];

export function Testimonials() {
  return (
    <section className="bg-[color:var(--color-bg-mist)] py-20 sm:py-28 lg:py-[160px]">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-20">
        <motion.div
          className="mb-12 flex max-w-[1280px] flex-col gap-6 sm:mb-16 lg:mb-[72px]"
          initial={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ margin: '-100px', once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3">
            <div className="h-px w-8 bg-[color:var(--color-amethyst)]" />
            <span className="text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-amethyst)]">
              From real churches
            </span>
          </div>
          <h2 className="font-bold text-[color:var(--color-ink)] [letter-spacing:-0.032em] [line-height:1.05]">
            <span className="block text-[clamp(40px,5vw,72px)]">
              Pastors who run this every
            </span>
            <span className="block text-[clamp(40px,5vw,72px)] font-light italic [font-family:var(--font-serif)] [letter-spacing:-0.025em]">
              week, in real ministry.
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={t.church} delay={i * 0.08} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ delay, t }: { t: Testimonial; delay: number }) {
  return (
    <motion.figure
      className="flex h-full min-h-[360px] flex-col gap-8 rounded-[20px] bg-white p-8 ring-1 ring-[color:var(--color-border-soft)] transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(15,11,26,0.18)] sm:p-10 lg:min-h-[400px]"
      initial={{ opacity: 0, y: 24 }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ margin: '-50px', once: true }}
      whileHover={{ y: -4 }}
      whileInView={{ opacity: 1, y: 0 }}
    >
      {/* Logo */}
      <div className="flex h-9 items-center">
        <Image
          alt={t.logoAlt}
          className="h-9 w-auto object-contain opacity-90"
          height={36}
          src={t.logo}
          unoptimized={t.logo.endsWith('.svg')}
          width={140}
        />
      </div>

      {/* Quote */}
      <blockquote className="flex-1">
        <p className="text-[17px] leading-[1.55] text-[color:var(--color-ink)]">
          &ldquo;{t.quote}&rdquo;
        </p>
      </blockquote>

      {/* Attribution */}
      <figcaption className="flex flex-col gap-0.5 border-t border-[color:var(--color-border-soft)] pt-5">
        <span className="text-[15px] font-semibold text-[color:var(--color-ink)]">
          {t.name}
        </span>
        <span className="text-[13px] text-[color:var(--color-gray-dark)]">
          {t.role} · {t.church}
        </span>
        <span className="text-[12px] text-[color:var(--color-gray-med)]">
          {t.size}
        </span>
      </figcaption>
    </motion.figure>
  );
}
